import React, { useState } from 'react';
import {
  Typography,
  Box,
  Tooltip
} from '@mui/material';
import {
  Info,
  History
} from '@mui/icons-material';
import ChangelogModal from '../Changelog/ChangelogModal';
import { VERSION_CONFIG } from '../../config/version';
import { getEnvironmentBadge } from '../../config/environment';

interface VersionDisplayProps {
  variant?: 'login' | 'navbar';
  showIcon?: boolean;
  showChangelog?: boolean;
}

const VersionDisplay: React.FC<VersionDisplayProps> = ({ 
  variant = 'navbar', 
  showIcon = false,
  showChangelog = true 
}) => {
  const [changelogOpen, setChangelogOpen] = useState(false);

  const handleVersionClick = () => {
    if (showChangelog) {
      setChangelogOpen(true);
    }
  };

  // Detection lives in config/environment.ts (single source — also drives the
  // AppBar pill, the favicon badge, and the non-prod login warning).
  // Uppercased `label` reproduces the historical strings here ('DEV' / 'STAGE'),
  // which differ from the favicon's shorter 'STG'.
  const envLabel = getEnvironmentBadge()?.label.toUpperCase() ?? null; // null in production

  // Build a detailed tooltip showing all build metadata
  const tooltipContent = [
    `Version: v${VERSION_CONFIG.version}`,
    `Build: ${VERSION_CONFIG.commitHash}`,
    `Built: ${VERSION_CONFIG.buildDate}`,
    envLabel ? `Environment: ${envLabel}` : null,
    showChangelog ? 'Click to view changelog' : null,
  ].filter(Boolean).join('\n');

  const getDisplayStyle = () => {
    switch (variant) {
      case 'login':
        return {
          position: 'fixed' as const,
          bottom: 16,
          right: 16,
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          padding: '8px 12px',
          borderRadius: '8px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          cursor: showChangelog ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          '&:hover': showChangelog ? {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          } : {}
        };
      case 'navbar':
        return {
          padding: '6px 10px',
          borderRadius: '6px',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          cursor: showChangelog ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          textAlign: 'left' as const,
          width: '100%',
          '&:hover': showChangelog ? {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          } : {}
        };
      default:
        return {};
    }
  };

  return (
    <>
      <Box
        sx={getDisplayStyle()}
        onClick={handleVersionClick}
      >
        <Tooltip title={tooltipContent} placement="right" arrow>
          <Box>
            {/* Line 1: Version + Environment */}
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 600,
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                lineHeight: 1.4,
              }}
            >
              {showIcon && <Info sx={{ fontSize: '0.75rem' }} />}
              v{VERSION_CONFIG.version}
              {envLabel && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    px: 0.6,
                    py: 0.1,
                    borderRadius: '4px',
                    lineHeight: 1.3,
                    backgroundColor:
                      envLabel === 'DEV'
                        ? 'error.main'
                        : envLabel === 'STAGE'
                        ? 'warning.main'
                        : 'transparent',
                    color:
                      envLabel === 'DEV'
                        ? '#fff'
                        : envLabel === 'STAGE'
                        ? '#000'
                        : 'text.secondary',
                  }}
                >
                  {envLabel}
                </Box>
              )}
              {showChangelog && <History sx={{ fontSize: '0.75rem', ml: 'auto' }} />}
            </Typography>

            {/* Line 2: Commit hash + build date */}
            <Typography
              sx={{
                fontSize: '0.6rem',
                fontWeight: 400,
                color: 'text.disabled',
                lineHeight: 1.4,
                mt: 0.2,
              }}
            >
              {VERSION_CONFIG.commitHash !== 'dev' && (
                <Box component="span" sx={{ fontFamily: 'monospace', mr: 0.5 }}>
                  #{VERSION_CONFIG.commitHash}
                </Box>
              )}
              {VERSION_CONFIG.buildDate !== 'local' ? VERSION_CONFIG.buildDate : 'Local build'}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      <ChangelogModal
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
      />
    </>
  );
};

export default VersionDisplay;
